/**
 * Full guest-registry export for disaster recovery. JSON is easy to inspect, diff, and re-import.
 */

export const WEDDING_ADMIN_BACKUP_VERSION = 1;

/**
 * @param {unknown[]} guests
 * @param {string[]} customTagPresets
 */
export function buildGuestRegistryBackupPayload(guests, customTagPresets = []) {
  return {
    weddingAdminBackupVersion: WEDDING_ADMIN_BACKUP_VERSION,
    exportedAt: new Date().toISOString(),
    guests: Array.isArray(guests) ? guests : [],
    customTagPresets: Array.isArray(customTagPresets)
      ? customTagPresets.map((x) => String(x).trim()).filter(Boolean)
      : [],
    /** Voice notes / mail drafts are not in this file; only the guest registry document payload + tag presets. */
    note:
      'To re-populate: merge `guests` into your canonical registry (e.g. Firestore `guests` field) or import rows via CSV. Custom tag presets are in `customTagPresets` (browser localStorage key weddingAdminCustomTagPresetsV1).',
  };
}

/**
 * @param {unknown[]} guests
 * @param {string[]} customTagPresets
 */
export function downloadGuestRegistryBackup(guests, customTagPresets) {
  const payload = buildGuestRegistryBackupPayload(guests, customTagPresets);
  const text = `${JSON.stringify(payload, null, 2)}\n`;
  const blob = new Blob([text], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const d = new Date();
  const dateStr = d.toISOString().slice(0, 10);
  const timeStr = d.toISOString().slice(11, 19).replace(/:/g, '-');
  a.href = url;
  a.download = `wedding-guest-registry-backup-${dateStr}-${timeStr}.json`;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * @param {string} text
 * @returns {{ guests: unknown[]; customTagPresets: string[] }}
 */
export function parseGuestRegistryBackup(text) {
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error('That file is not valid JSON.');
  }
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid backup file structure.');
  }
  const ver = data.weddingAdminBackupVersion;
  if (ver !== WEDDING_ADMIN_BACKUP_VERSION) {
    throw new Error(
      ver == null
        ? 'This file does not look like a wedding admin backup (missing version).'
        : `Unsupported backup version: ${ver}. Expected ${WEDDING_ADMIN_BACKUP_VERSION}.`,
    );
  }
  if (!Array.isArray(data.guests)) {
    throw new Error('Backup has no guests array.');
  }
  const customTagPresets = Array.isArray(data.customTagPresets)
    ? data.customTagPresets.map((x) => String(x).trim()).filter(Boolean)
    : [];
  return { guests: data.guests, customTagPresets };
}
